import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, MinusCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { logError } from 'services/logging/logging';
import { WebEDIError } from 'services/webediErrors/types';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { GenericError } from 'support/helpers/errors/errors';
import { classNames } from 'support/helpers/generic/generic';
import { TransactionsRoutes } from 'support/types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Button } from 'components/Form/Button/Button';
import { InfoPopoverBody, InfoPopoverFooter } from 'components/InfoPopover/InfoPopover';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';
import { Translate } from 'components/Translate/Translate';

export const WithErrorsContent = ({
  isErrorFixed,
  error,
  numberOfErrors,
  isValidating,
  isPendingValidation,
  viewMode,
  isPartnerIssueResolver,
  fieldDescription,
}: {
  isErrorFixed: boolean;
  error: WebEDIError;
  numberOfErrors: number;
  isValidating?: boolean;
  isPendingValidation?: boolean;
  viewMode: boolean;
  isPartnerIssueResolver: boolean;
  fieldDescription: string | undefined;
}) => {
  const { id: messageId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { service } = useErrorContext<WebEDIError>();
  const showNextErrorButton = numberOfErrors > 1 || ((isErrorFixed || isPendingValidation) && numberOfErrors > 0);
  const currentError = service.useCurrentError();

  const moveToNextError = () => {
    track('webedi:next_error', { error: currentError });
    service.moveToNextError();
  };

  const focusOnNextNonFixedError = () => {
    track('webedi:next_error', { error: currentError });
    service.focusOnCurrentError();
  };

  return (
    <>
      <InfoPopoverBody>
        <div className="flex justify-between gap-2">
          {isErrorFixed ? (
            <h1 className="text-sm font-bold">{t('webedi:errorModal.withErrors.title.errorFixed')}</h1>
          ) : (
            <h1 className="text-sm font-bold text-red-700">{error.title}</h1>
          )}
          <div className="flex size-5 items-center">
            {isPendingValidation ? <MinusCircleIcon className="size-6 shrink-0 text-blue-500" /> : null}
            {!isPendingValidation && isValidating && error ? <LoadingSpinner sizeClassName="size-5" /> : null}
            {!isPendingValidation && isErrorFixed && !isValidating ? (
              <CheckCircleIcon className="size-6 shrink-0 text-green-500" />
            ) : null}
          </div>
        </div>

        {Boolean(isErrorFixed || error.description) && (
          <p className="text-sm text-gray-700">
            {isErrorFixed ? (
              t('webedi:errorModal.withErrors.description.errorFixed')
            ) : (
              <Translate i18nKey={error.description} />
            )}
          </p>
        )}

        {fieldDescription && !isErrorFixed ? (
          <div>
            <div className="mt-2 rounded-md bg-gray-50 px-3 py-2">
              <p className="text-sm text-gray-600">
                <Translate i18nKey={'webedi:errorModal.withErrors.fieldDescription'} values={{ fieldDescription }} />
              </p>
            </div>
          </div>
        ) : null}

        {isPendingValidation ? (
          <div className="mt-2 rounded-md bg-blue-50 px-3 py-2">
            <p className="text-sm font-semibold text-blue-700">{t('webedi:errorModal.pendingValidation.title')}</p>
            <p className="text-sm text-blue-600">{t('webedi:errorModal.pendingValidation.description')}</p>
          </div>
        ) : null}
      </InfoPopoverBody>
      <InfoPopoverFooter>
        {viewMode ? (
          isPartnerIssueResolver ? (
            <Button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!messageId) {
                  logError(new GenericError('messageId is undefined'));
                  return;
                }
                // we need to navigate to the message edit page and focus on the error field
                navigate(generatePath(TransactionsRoutes.edit, { id: messageId }), {
                  state: {
                    focusOnField: error,
                  },
                });
              }}
              variant="secondary-danger"
              RightIcon={PencilSquareIcon}
              size="extra-small"
              analyticsId="webedi:fix_issue"
            >
              {t('webedi:viewDocument.errorPopover.fixIssueCTA')}
            </Button>
          ) : (
            <span></span>
          )
        ) : (
          <Badge type="indicator" color={BadgeColor.red}>
            {viewMode
              ? t('webedi:errorModal.totalErrorsInDocument', { count: numberOfErrors, numberOfErrors })
              : t('webedi:errorModal.errorsRemaining', { count: numberOfErrors, numberOfErrors })}
          </Badge>
        )}

        <Button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={isErrorFixed ? focusOnNextNonFixedError : moveToNextError}
          variant="minimal-danger"
          RightIcon={ChevronRightIcon}
          size="extra-small"
          className={classNames(showNextErrorButton ? 'visible' : 'invisible')}
          analyticsId="webedi:next_error"
        >
          {t('webedi:errorModal.nextError')}
        </Button>
      </InfoPopoverFooter>
    </>
  );
};
